import { create } from 'zustand'
import { platformApi, setOperatorToken } from './api'
import type { Operator } from './types'

/**
 * The operator session.
 *
 * <p>Lives inside the feature rather than in src/stores, because it is the only thing that
 * may reach this feature's api module — the lint rule that says a feature is imported through
 * its barrel is the same rule that keeps the two realms' clients from being mixed up.
 *
 * <p>Separate from the tenant authStore rather than a flag on it. The two realms have different
 * tokens, different lifetimes, different endpoints and different failure modes, and one store
 * holding both would eventually let a tenant session be mistaken for an operator one by a
 * component that checked the wrong field.
 *
 * <p>Nothing is persisted. A reload signs the operator out, which is the same decision the
 * API client makes about the token and for the same reason.
 */
interface PlatformAuthState {
  operator: Operator | null
  mustChangePassword: boolean
  login: (email: string, password: string) => Promise<void>
  logout: () => void
}

export const usePlatformAuthStore = create<PlatformAuthState>((set) => ({
  operator: null,
  mustChangePassword: false,

  login: async (email, password) => {
    const response = await platformApi.login(email, password)
    setOperatorToken(response.accessToken)
    set({ operator: response.operator, mustChangePassword: response.mustChangePassword })
  },

  logout: () => {
    setOperatorToken(null)
    set({ operator: null, mustChangePassword: false })
  },
}))
